import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import { fade } from "@material-ui/core/styles";
import { ArrowBack as ArrowBackIcon, Refresh as RefreshIcon } from "@material-ui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";

import { AdminContainer } from "../components/layouts";
import ConfirmationDialog from "../components/ConfirmationDialog";
import Time from "../lithic/Moment";
import api from "../api";
import { toast } from "react-toastify";
import { useToastError } from "../components/FormError";
import useToggle from "../state/useToggle";

const CANONICAL_FIELDS = [
  { value: "ignore", label: "(Ignore)" },
  { value: "first_name", label: "First Name" },
  { value: "last_name", label: "Last Name" },
  { value: "name_combined", label: "Full Name (combined)" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "external_id", label: "External ID" },
  { value: "source", label: "Source" },
  { value: "property_name", label: "Property Name" },
  { value: "unit_title", label: "Unit Title" },
  { value: "message", label: "Message" },
  { value: "desired_move_in_date", label: "Desired Move-in Date" }
];

const STATUS_LABELS = {
  pending: "Pending",
  awaiting_confirmation: "Awaiting Review",
  processing: "Processing",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled"
};

const POLL_INTERVAL_MS = 2500;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const REASON_LABELS = {
  no_identity_field: "Missing email and phone",
  invalid_email: "Invalid email format",
  invalid_phone: "Invalid phone number",
  duplicate_in_file: "Duplicate of an earlier row",
  row_crashed: "Row processing crashed",
  unknown: "Unknown error"
};

function humanizeReason(reason) {
  if (!reason) return null;
  return REASON_LABELS[reason] || reason;
}

function extractApiError(error, fallback) {
  if (!error) return fallback || "An unexpected error occurred";
  if (typeof error === "string") return error;
  const data = error.response?.data;
  if (data) {
    if (typeof data === "string") return data;
    if (data.error) {
      if (typeof data.error === "string") return data.error;
      if (data.error.message) return data.error.message;
    }
    if (data.message) return data.message;
  }
  return error.message || fallback || "An unexpected error occurred";
}

export default function LeadImportsPage() {
  const { id: partnerId } = useParams();
  const classes = useStyles();

  const [partner, setPartner] = useState({});
  const [selectedImportId, setSelectedImportId] = useState(null);

  useEffect(() => {
    if (!partnerId) return;
    api
      .getPartner({ id: partnerId })
      .then(api.pickData)
      .then(setPartner)
      .catch(() => {});
  }, [partnerId]);

  if (selectedImportId) {
    return (
      <LeadImportDetail
        partnerId={partnerId}
        partnerName={partner.name}
        importId={selectedImportId}
        onBack={() => setSelectedImportId(null)}
      />
    );
  }

  return (
    <LeadImportsList
      partnerId={partnerId}
      partnerName={partner.name}
      onSelect={(id) => setSelectedImportId(id)}
      classes={classes}
    />
  );
}

function StatusChip({ status }) {
  const classes = useStyles();
  const cls = classes[`status_${status}`];
  return (
    <Chip
      size="small"
      label={STATUS_LABELS[status] || status}
      className={`${classes.statusChip} ${cls || ""}`}
    />
  );
}

function ListBodyRows({ loading, items, onSelect, classes }) {
  if (loading && items.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={8} align="center">
          <CircularProgress size={24} />
        </TableCell>
      </TableRow>
    );
  }
  if (items.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={8} align="center">
          <Typography variant="body2" color="textSecondary">
            No imports yet.
          </Typography>
        </TableCell>
      </TableRow>
    );
  }
  return items.map((row) => (
    <TableRow
      hover
      key={row.id}
      className={classes.clickableRow}
      onClick={() => onSelect(row.id)}
    >
      <TableCell>{row.id}</TableCell>
      <TableCell>
        <StatusChip status={row.status} />
      </TableCell>
      <TableCell>{row.csvFilename || "—"}</TableCell>
      <TableCell align="right">{row.csvRowCount ?? "—"}</TableCell>
      <TableCell align="right" className={classes.successCell}>
        {row.summary?.success ?? "—"}
      </TableCell>
      <TableCell align="right" className={classes.failedCell}>
        {row.summary?.failed ?? "—"}
      </TableCell>
      <TableCell>
        {row.createdAt ? (
          <Time formatExact="MMM DD, YYYY HH:mm">{row.createdAt}</Time>
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell>
        {row.completedAt ? (
          <Time formatExact="MMM DD, YYYY HH:mm">{row.completedAt}</Time>
        ) : (
          "—"
        )}
      </TableCell>
    </TableRow>
  ));
}

function LeadImportsList({ partnerId, partnerName, onSelect, classes }) {
  const uploadToggle = useToggle();
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const toastError = useToastError();

  const refresh = useCallback(() => {
    if (!partnerId) return;
    setLoading(true);
    api
      .listLeadImports({ partnerId, page: page + 1, perPage })
      .then((resp) => {
        setItems(resp.data?.items || []);
        setTotalCount(resp.data?.totalCount ?? 0);
      })
      .catch((error) => toastError(extractApiError(error)))
      .finally(() => setLoading(false));
  }, [partnerId, page, perPage, toastError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AdminContainer variant="list">
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          <Button
            size="small"
            component={RouterLink}
            to={`/partners/${partnerId}`}
            startIcon={<ArrowBackIcon />}
          >
            Partner
          </Button>
          <Typography variant="h5" color="primary">
            {partnerName ? `${partnerName} — Lead Imports` : "Lead Imports"}
          </Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={refresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={uploadToggle.turnOn}
        >
          Import Leads
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Filename</TableCell>
                <TableCell align="right">Rows</TableCell>
                <TableCell align="right">Success</TableCell>
                <TableCell align="right">Failed</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Completed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <ListBodyRows
                loading={loading}
                items={items}
                onSelect={onSelect}
                classes={classes}
              />
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={perPage}
          rowsPerPageOptions={[25, 50, 100]}
          onChangePage={(_e, p) => setPage(p)}
          onChangeRowsPerPage={(e) => {
            setPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      <UploadDialog
        partnerId={partnerId}
        open={uploadToggle.on}
        onClose={uploadToggle.turnOff}
        onCreated={(created) => {
          uploadToggle.turnOff();
          refresh();
          if (created?.id) onSelect(created.id);
        }}
      />
    </AdminContainer>
  );
}

function UploadDialog({ partnerId, open, onClose, onCreated }) {
  const dialogClasses = useDialogStyles();
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const toastError = useToastError();

  useEffect(() => {
    if (open) {
      setFile(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleFile = (event) => {
    const [selected] = event.target.files || [];
    if (!selected) return;
    if (
      selected.type !== "text/csv" &&
      !selected.name.toLowerCase().endsWith(".csv")
    ) {
      toastError(new Error("Please select a CSV file."));
      event.target.value = null;
      return;
    }
    if (selected.size > MAX_FILE_SIZE_BYTES) {
      toastError(new Error("File exceeds 5MB limit."));
      event.target.value = null;
      return;
    }
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!file) {
      toastError(new Error("Select a CSV file first."));
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await api.createLeadImport({ partnerId, formData });
      toast.success("CSV uploaded. Review the column mapping next.");
      onCreated(resp.data);
    } catch (error) {
      toastError(extractApiError(error, "Failed to upload CSV."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmationDialog
      open={open}
      onCancel={onClose}
      onConfirm={handleSubmit}
      title="Import leads from CSV"
      cancelProps={{ children: "Cancel" }}
      confirmProps={{
        children: submitting ? "Uploading..." : "Upload",
        disabled: submitting || !file
      }}
    >
      <Box className={dialogClasses.content}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Upload a CSV with lead data. Limits: 5 MB, 10,000 rows. After upload
          you will review the column mapping before any leads are created.
        </Typography>

        <Box className={dialogClasses.fileRow}>
          <input
            accept=".csv,text/csv"
            id="lead-import-file"
            type="file"
            style={{ display: "none" }}
            onChange={(e) => {
              handleFile(e);
              e.target.value = "";
            }}
          />
          <label htmlFor="lead-import-file">
            <Button variant="outlined" component="span">
              {file ? file.name : "Select CSV"}
            </Button>
          </label>
          {file ? (
            <Typography variant="caption" color="textSecondary">
              {(file.size / 1024).toFixed(1)} KB
            </Typography>
          ) : null}
        </Box>
      </Box>
    </ConfirmationDialog>
  );
}

function LeadImportDetail({ partnerId, partnerName, importId, onBack }) {
  const classes = useStyles();
  const [leadImport, setLeadImport] = useState(null);
  const [loading, setLoading] = useState(true);
  const toastError = useToastError();
  const pollRef = useRef(null);

  const fetchOnce = useCallback(
    (showSpinner = false) => {
      if (showSpinner) setLoading(true);
      return api
        .getLeadImport({ partnerId, id: importId })
        .then((resp) => {
          setLeadImport(resp.data);
          return resp.data;
        })
        .catch((error) => {
          toastError(extractApiError(error, "Failed to load import."));
        })
        .finally(() => {
          if (showSpinner) setLoading(false);
        });
    },
    [partnerId, importId, toastError]
  );

  useEffect(() => {
    fetchOnce(true);
  }, [fetchOnce]);

  useEffect(() => {
    if (!leadImport) return;
    if (leadImport.status === "processing" || leadImport.status === "pending") {
      pollRef.current = setInterval(() => fetchOnce(false), POLL_INTERVAL_MS);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
      };
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return undefined;
  }, [leadImport, fetchOnce]);

  if (loading && !leadImport) {
    return (
      <AdminContainer variant="list">
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </AdminContainer>
    );
  }

  if (!leadImport) {
    return (
      <AdminContainer variant="list">
        <Box mt={4}>
          <Button onClick={onBack} startIcon={<ArrowBackIcon />}>
            Back
          </Button>
          <Typography color="error">Could not load import.</Typography>
        </Box>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer variant="list">
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          <Button onClick={onBack} startIcon={<ArrowBackIcon />} size="small">
            All Imports
          </Button>
          <Typography variant="h5" color="primary">
            {partnerName ? `${partnerName} — ` : ""}Lead Import #
            {leadImport.id}
          </Typography>
          <StatusChip status={leadImport.status} />
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => fetchOnce(false)}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper className={classes.summaryPaper}>
        <SummaryRow label="Filename" value={leadImport.csvFilename || "—"} />
        <SummaryRow
          label="Total rows"
          value={leadImport.csvRowCount ?? "—"}
        />
        <SummaryRow
          label="Created"
          value={
            leadImport.createdAt ? (
              <Time formatExact="MMM DD, YYYY HH:mm:ss">
                {leadImport.createdAt}
              </Time>
            ) : (
              "—"
            )
          }
        />
        {leadImport.completedAt ? (
          <SummaryRow
            label="Completed"
            value={
              <Time formatExact="MMM DD, YYYY HH:mm:ss">
                {leadImport.completedAt}
              </Time>
            }
          />
        ) : null}
      </Paper>

      {leadImport.status === "awaiting_confirmation" ? (
        <MappingReview
          leadImport={leadImport}
          partnerId={partnerId}
          onUpdated={(updated) => setLeadImport(updated)}
          onCommitted={() => fetchOnce(false)}
          onCancelled={() => fetchOnce(false)}
        />
      ) : (
        <ResultView leadImport={leadImport} />
      )}
    </AdminContainer>
  );
}

function SummaryRow({ label, value }) {
  const classes = useStyles();
  return (
    <Box className={classes.summaryRow}>
      <Typography variant="subtitle2">{label}</Typography>
      <Typography component="div">{value}</Typography>
    </Box>
  );
}

function MappingReview({
  leadImport,
  partnerId,
  onUpdated,
  onCommitted,
  onCancelled
}) {
  const classes = useStyles();
  const toastError = useToastError();
  const [saving, setSaving] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const commitConfirm = useToggle();
  const cancelConfirm = useToggle();

  const headers = leadImport.parsedHeaders || [];
  const initialMapping = useMemo(() => {
    const proposed = leadImport.confirmedMapping?.length
      ? leadImport.confirmedMapping
      : leadImport.proposedMapping || [];
    const byColumn = {};
    proposed.forEach((m) => {
      byColumn[m.column] = m.canonicalField || "ignore";
    });
    const result = {};
    headers.forEach((h) => {
      result[h] = byColumn[h] || "ignore";
    });
    return result;
  }, [leadImport, headers]);

  const [mapping, setMapping] = useState(initialMapping);
  const [previewRows, setPreviewRows] = useState(
    leadImport.previewRows || []
  );

  useEffect(() => {
    setMapping(initialMapping);
    setPreviewRows(leadImport.previewRows || []);
  }, [initialMapping, leadImport.previewRows]);

  const buildPayload = () =>
    headers.map((h) => ({
      column: h,
      canonicalField: mapping[h] || "ignore"
    }));

  const handleStaleStatus = (error) => {
    const msg = extractApiError(error);
    if (msg && msg.includes("not_in_review_phase")) {
      toast.info("This import has already moved on. Refreshing…");
      onCommitted();
      return true;
    }
    return false;
  };

  const handleRefreshPreview = async () => {
    setSaving(true);
    try {
      const resp = await api.updateLeadImportMapping({
        partnerId,
        id: leadImport.id,
        mapping: buildPayload()
      });
      setPreviewRows(resp.data?.previewRows || []);
      onUpdated(resp.data);
      toast.success("Mapping saved. Preview refreshed.");
    } catch (error) {
      if (!handleStaleStatus(error)) {
        toastError(extractApiError(error, "Failed to save mapping."));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCommit = async () => {
    commitConfirm.turnOff();
    if (committing) return;
    setCommitting(true);
    try {
      await api.updateLeadImportMapping({
        partnerId,
        id: leadImport.id,
        mapping: buildPayload()
      });
      await api.commitLeadImport({ partnerId, id: leadImport.id });
      toast.success("Import started.");
      onCommitted();
    } catch (error) {
      if (!handleStaleStatus(error)) {
        toastError(extractApiError(error, "Failed to commit import."));
      }
    } finally {
      setCommitting(false);
    }
  };

  const handleCancel = async () => {
    cancelConfirm.turnOff();
    setCancelling(true);
    try {
      await api.cancelLeadImport({ partnerId, id: leadImport.id });
      toast.success("Import cancelled.");
      onCancelled();
    } catch (error) {
      toastError(extractApiError(error, "Failed to cancel import."));
    } finally {
      setCancelling(false);
    }
  };

  const mappedFieldCount = Object.values(mapping).filter(
    (v) => v && v !== "ignore"
  ).length;

  const previewColumns = useMemo(() => {
    const seen = new Set();
    const cols = [];
    headers.forEach((h) => {
      const cf = mapping[h];
      if (!cf || cf === "ignore") return;
      if (seen.has(cf)) return;
      seen.add(cf);
      cols.push(cf);
    });
    return cols;
  }, [headers, mapping]);

  return (
    <>
      <Paper className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Typography variant="h6">Column Mapping</Typography>
            <Chip
              size="small"
              variant="outlined"
              label={`${mappedFieldCount} of ${headers.length} mapped`}
            />
          </Box>
          <Typography variant="body2" color="textSecondary">
            Match each column from your CSV to a lead field. Unmapped columns
            will be ignored.
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>CSV Column</TableCell>
                <TableCell>Maps to</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {headers.map((header) => (
                <TableRow key={header}>
                  <TableCell className={classes.headerCell}>
                    {header}
                  </TableCell>
                  <TableCell>
                    <FormControl
                      variant="outlined"
                      size="small"
                      style={{ minWidth: 240 }}
                    >
                      <InputLabel>Field</InputLabel>
                      <Select
                        value={mapping[header] || "ignore"}
                        label="Field"
                        onChange={(e) =>
                          setMapping((prev) => ({
                            ...prev,
                            [header]: e.target.value
                          }))
                        }
                      >
                        {CANONICAL_FIELDS.map((f) => (
                          <MenuItem key={f.value} value={f.value}>
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box className={classes.actionBar}>
          <Button
            variant="outlined"
            onClick={handleRefreshPreview}
            disabled={saving || committing || cancelling}
          >
            {saving ? "Saving..." : "Save & Preview"}
          </Button>
          <Box display="flex" style={{ gap: 8 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={cancelConfirm.turnOn}
              disabled={cancelling || committing}
            >
              {cancelling ? "Cancelling..." : "Cancel Import"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={commitConfirm.turnOn}
              disabled={committing || cancelling || mappedFieldCount === 0}
            >
              {committing ? "Committing..." : "Save & Commit"}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant="h6">Preview</Typography>
          <Typography variant="body2" color="textSecondary">
            First {previewRows.length} rows applied with the current mapping.
          </Typography>
        </Box>
        {previewColumns.length === 0 ? (
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">
              No columns mapped yet. Pick at least one field above.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {previewColumns.map((col) => (
                    <TableCell key={col}>
                      {CANONICAL_FIELDS.find((f) => f.value === col)?.label ||
                        col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={previewColumns.length}
                      align="center"
                    >
                      <Typography variant="body2" color="textSecondary">
                        No preview rows. Click Save & Preview after changing
                        the mapping.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  previewRows.map((row, idx) => (
                    <TableRow key={idx}>
                      {previewColumns.map((col) => {
                        // server returns camelCase keys; map snake_case → camelCase
                        const camel = col.replace(/_([a-z])/g, (_, c) =>
                          c.toUpperCase()
                        );
                        return (
                          <TableCell key={col}>
                            {row[camel] ?? row[col] ?? ""}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <ConfirmationDialog
        open={commitConfirm.on}
        onCancel={commitConfirm.turnOff}
        onConfirm={handleCommit}
        title="Start importing leads?"
        confirmProps={{ children: "Start Import", color: "primary" }}
      >
        <Typography variant="body2">
          {`Importing ${leadImport.csvRowCount ?? "?"} row(s) with `}
          {`${mappedFieldCount} mapped field(s). `}
          Leads matching existing records will be updated; new records will
          be created. This cannot be undone.
        </Typography>
      </ConfirmationDialog>

      <ConfirmationDialog
        open={cancelConfirm.on}
        onCancel={cancelConfirm.turnOff}
        onConfirm={handleCancel}
        title="Cancel this import?"
        confirmProps={{ children: "Cancel Import", color: "secondary" }}
        cancelProps={{ children: "Keep" }}
      >
        <Typography variant="body2">
          The CSV will be discarded and no leads will be created.
        </Typography>
      </ConfirmationDialog>
    </>
  );
}

function ResultView({ leadImport }) {
  const classes = useStyles();
  const summary = leadImport.summary || {};
  const errors = leadImport.errors || [];
  const isProcessing = leadImport.status === "processing";

  return (
    <>
      <Paper className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant="h6">Summary</Typography>
          {isProcessing ? (
            <Typography variant="body2" color="textSecondary">
              Processing… status will refresh automatically.
            </Typography>
          ) : null}
        </Box>
        <Box className={classes.summaryGrid}>
          <SummaryStat label="Total" value={summary.total ?? "—"} />
          <SummaryStat label="Processed" value={summary.processed ?? "—"} />
          <SummaryStat
            label="Created"
            value={summary.created ?? "—"}
            tone="success"
          />
          <SummaryStat
            label="Updated"
            value={summary.updated ?? "—"}
            tone="info"
          />
          <SummaryStat
            label="Failed"
            value={summary.failed ?? "—"}
            tone="failed"
          />
        </Box>
      </Paper>

      {errors.length > 0 ? (
        <Paper className={classes.section}>
          <Box className={classes.sectionHeader}>
            <Typography variant="h6" color="error">
              Errors ({errors.length})
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Rows that could not be imported. Other rows succeeded.
            </Typography>
          </Box>
          <TableContainer className={classes.errorTable}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width={80}>Row</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((err, idx) => {
                  const meta = Object.entries(err)
                    .filter(([k]) => k !== "reason" && k !== "id")
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(", ");
                  return (
                    <TableRow key={idx} hover>
                      <TableCell>{err.id ?? idx + 1}</TableCell>
                      <TableCell className={classes.errorReason}>
                        {humanizeReason(err.reason) || "(no reason)"}
                      </TableCell>
                      <TableCell className={classes.errorMeta}>
                        {meta || "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}
    </>
  );
}

function SummaryStat({ label, value, tone }) {
  const classes = useStyles();
  let valueClass = null;
  let boxClass = classes.summaryStat;
  if (tone === "success") {
    valueClass = classes.successCell;
    boxClass = `${boxClass} ${classes.summaryStatSuccess}`;
  } else if (tone === "failed") {
    valueClass = classes.failedCell;
    boxClass = `${boxClass} ${classes.summaryStatFailed}`;
  } else if (tone === "info") {
    valueClass = classes.infoCell;
    boxClass = `${boxClass} ${classes.summaryStatInfo}`;
  }
  return (
    <Box className={boxClass}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="h5" className={valueClass}>
        {value}
      </Typography>
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  header: {
    margin: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap"
  },
  clickableRow: {
    cursor: "pointer"
  },
  successCell: {
    color: theme.palette.success.main,
    fontWeight: 500
  },
  failedCell: {
    color: theme.palette.error.main,
    fontWeight: 500
  },
  summaryPaper: {
    margin: theme.spacing(2),
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    paddingBottom: theme.spacing(0.5),
    "&:not(:last-child)": {
      borderBottom: `1px solid ${theme.palette.grey[200]}`
    }
  },
  section: {
    margin: theme.spacing(2),
    padding: theme.spacing(2)
  },
  sectionHeader: {
    marginBottom: theme.spacing(1)
  },
  headerCell: {
    fontFamily: "monospace",
    fontWeight: 500
  },
  actionBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(2),
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, 1fr)"
    }
  },
  summaryStat: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 6,
    backgroundColor: theme.palette.background.default
  },
  summaryStatSuccess: {
    backgroundColor: fade(theme.palette.success.main, 0.1),
    borderColor: fade(theme.palette.success.main, 0.3)
  },
  summaryStatFailed: {
    backgroundColor: fade(theme.palette.error.main, 0.1),
    borderColor: fade(theme.palette.error.main, 0.3)
  },
  summaryStatInfo: {
    backgroundColor: fade(theme.palette.info.main, 0.1),
    borderColor: fade(theme.palette.info.main, 0.3)
  },
  infoCell: {
    color: theme.palette.info.dark,
    fontWeight: 500
  },
  errorTable: {
    maxHeight: 480
  },
  errorReason: {
    fontWeight: 500,
    color: theme.palette.error.dark
  },
  errorMeta: {
    fontFamily: "monospace",
    fontSize: 12,
    color: theme.palette.text.secondary
  },
  statusChip: {
    fontWeight: 500,
    textTransform: "capitalize"
  },
  status_pending: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary
  },
  status_awaiting_confirmation: {
    backgroundColor: fade(theme.palette.warning.main, 0.2),
    color: theme.palette.warning.dark
  },
  status_processing: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText
  },
  status_completed: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText
  },
  status_failed: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText
  },
  status_cancelled: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.text.secondary
  }
}));

const useDialogStyles = makeStyles((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 400,
    padding: theme.spacing(1)
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2)
  }
}));
